import i18next from 'i18next';
import { formatAuthorName } from 'utils/formatters';
import { DateFormatter } from 'infrastructure/functions';
import BaseTableOptionsDropdown from 'components/atoms/base-table-option-dropdown';
import { DateFormats } from 'infrastructure/enums';
import { convertSecondsToTime } from 'utils/timeHelper';
import { DOCUMENTATION_TIME_PLACEHOLDER } from 'infrastructure/consts/tables';
import { tableSortMap } from 'infrastructure/antd/functions/create-sort-order';

import type { SortState } from 'infrastructure/types';
import type {
  IMedicalHistory,
  IMedicalHistoryAuthor,
  IUpdateMedicalHistory,
} from 'infrastructure/interfaces/i-medical-history';
import type { IBaseTableColumns } from 'components/atoms/base-table/types';

type TArgs = Omit<IUpdateMedicalHistory, 'patientGuid'>;

interface Props {
  setHistoryForUpdate: (args: TArgs) => void;
  loading: boolean;
  updatedItemGuid?: string;
  disableActions?: boolean;
  documentationTimeSort: SortState;
}
export const columns = ({
  setHistoryForUpdate,
  loading,
  updatedItemGuid,
  disableActions,
  documentationTimeSort,
}: Props): IBaseTableColumns<IMedicalHistory> => {
  return [
    {
      label: i18next.t('labels.lastUpdatedDate'),
      key: 'updatedAt',
      width: '120px',
      render(date: string) {
        return (
          <span data-cy="past-medical-history-updatedAt">
            {DateFormatter({
              date,
              format: DateFormats['MM-dd-yyyy h:mm a'],
            })}
          </span>
        );
      },
    },
    {
      label: i18next.t('labels.documentationTime'),
      key: 'documentationTime',
      width: '150px',
      render(documentationTime: number | null) {
        const actionTime = !documentationTime
          ? DOCUMENTATION_TIME_PLACEHOLDER
          : convertSecondsToTime(documentationTime);
        return <span>{actionTime}</span>;
      },
      align: 'center',
      sortable: true,
      sortKey: 'documentationTime',
      sortOrder: tableSortMap[documentationTimeSort],
    },
    {
      label: i18next.t(
        'labels.medicalIssueSurgeryAndOrHospitalizationHistoryDescription',
      ),
      key: 'record',
      width: '500px',
      render(value: string) {
        return <span data-cy="past-medical-history-record">{value}</span>;
      },
    },
    {
      label: i18next.t('labels.electronicallySignedBy'),
      key: 'author',
      width: '300px',
      render(author: IMedicalHistoryAuthor) {
        return (
          <span data-cy="past-medical-history-author">
            {formatAuthorName(author)}
          </span>
        );
      },
      align: 'right',
    },
    {
      label: i18next.t('labels.option'),
      key: 'guid',
      width: '75px',
      render(value, { guid, record }) {
        return (
          <BaseTableOptionsDropdown
            loading={loading && guid === updatedItemGuid}
            onEdit={() => setHistoryForUpdate({ guid, record })}
            disabled={disableActions}
          />
        );
      },
    },
  ];
};
